import React, { useRef, useEffect, useState, useCallback } from 'react';
import './Explainability.css';

const TYPE_COLORS = {
  seed: '#22c55e',
  decision_outcome: '#3b82f6',
  concept: '#8b5cf6',
  fact: '#f59e0b',
  verbatim_chunk: '#10b981',
  and_gate: '#f97316',
  or_gate: '#f97316',
  reference: '#a855f7',
  metric: '#06b6d4',
  category: '#ec4899',
  constant: '#64748b',
};

function getNodeColor(nodeType) {
  return TYPE_COLORS[nodeType] || TYPE_COLORS.concept;
}

const ActivationGraph = ({
  seedNodes = [],
  firedNodes = [],
  synapses = [],
  width = 640,
  height = 480,
  onNodeClick,
}) => {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const nodeMap = useCallback(() => {
    const map = {};
    seedNodes.forEach(s => {
      map[s.node_id] = {
        id: s.node_id,
        name: s.node_name || s.node_id?.substring(0, 8),
        type: 'seed',
        potency: s.similarity || 0.8,
        spikeCount: 1,
        isSeed: true,
      };
    });
    firedNodes.forEach(n => {
      const existing = map[n.node_id];
      map[n.node_id] = {
        id: n.node_id,
        name: n.node_name || n.node_id?.substring(0, 8),
        type: existing?.isSeed ? 'seed' : (n.node_type || 'concept'),
        potency: n.potency || 0.5,
        spikeCount: n.spike_count || 1,
        isSeed: existing?.isSeed || false,
      };
    });
    return map;
  }, [seedNodes, firedNodes]);

  const validEdges = useCallback(() => {
    const nm = nodeMap();
    return synapses.filter(s => nm[s.source_id] && nm[s.target_id]);
  }, [synapses, nodeMap]);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    while (svg.firstChild) svg.removeChild(svg.firstChild);

    const nm = nodeMap();
    const nodes = Object.values(nm);
    const edges = validEdges();

    if (nodes.length === 0) return;

    // Force-directed layout (simple spring simulation)
    const positions = {};
    const velocities = {};
    nodes.forEach((n, i) => {
      const angle = (2 * Math.PI * i) / nodes.length;
      const r = Math.min(width, height) * 0.3;
      positions[n.id] = {
        x: width / 2 + r * Math.cos(angle) + (Math.random() - 0.5) * 20,
        y: height / 2 + r * Math.sin(angle) + (Math.random() - 0.5) * 20,
      };
      velocities[n.id] = { x: 0, y: 0 };
    });

    const ITERATIONS = 120;
    const REPULSION = 3000;
    const SPRING_K = 0.01;
    const SPRING_LEN = 100;
    const DAMPING = 0.85;
    const GRAVITY = 0.02;

    for (let iter = 0; iter < ITERATIONS; iter++) {
      // Repulsion
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = positions[nodes[i].id];
          const b = positions[nodes[j].id];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 1);
          const force = REPULSION / (dist * dist);
          const fx = (dx / dist) * force;
          const fy = (dy / dist) * force;
          velocities[nodes[i].id].x += fx;
          velocities[nodes[i].id].y += fy;
          velocities[nodes[j].id].x -= fx;
          velocities[nodes[j].id].y -= fy;
        }
      }

      // Spring (edges)
      edges.forEach(e => {
        const a = positions[e.source_id];
        const b = positions[e.target_id];
        if (!a || !b) return;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 1);
        const force = SPRING_K * (dist - SPRING_LEN);
        const fx = (dx / dist) * force;
        const fy = (dy / dist) * force;
        velocities[e.source_id].x += fx;
        velocities[e.source_id].y += fy;
        velocities[e.target_id].x -= fx;
        velocities[e.target_id].y -= fy;
      });

      // Gravity toward center
      nodes.forEach(n => {
        const p = positions[n.id];
        velocities[n.id].x += (width / 2 - p.x) * GRAVITY;
        velocities[n.id].y += (height / 2 - p.y) * GRAVITY;
      });

      // Apply velocities
      nodes.forEach(n => {
        const v = velocities[n.id];
        const p = positions[n.id];
        v.x *= DAMPING;
        v.y *= DAMPING;
        p.x += v.x;
        p.y += v.y;
        p.x = Math.max(30, Math.min(width - 30, p.x));
        p.y = Math.max(30, Math.min(height - 30, p.y));
      });
    }

    // SVG defs
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
    const marker = document.createElementNS('http://www.w3.org/2000/svg', 'marker');
    marker.setAttribute('id', 'act-arrow');
    marker.setAttribute('markerWidth', '8');
    marker.setAttribute('markerHeight', '6');
    marker.setAttribute('refX', '8');
    marker.setAttribute('refY', '3');
    marker.setAttribute('orient', 'auto');
    const polygon = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
    polygon.setAttribute('points', '0 0, 8 3, 0 6');
    polygon.setAttribute('fill', '#94a3b8');
    marker.appendChild(polygon);
    defs.appendChild(marker);

    // Seed glow filter
    const filter = document.createElementNS('http://www.w3.org/2000/svg', 'filter');
    filter.setAttribute('id', 'seed-glow');
    const feGauss = document.createElementNS('http://www.w3.org/2000/svg', 'feGaussianBlur');
    feGauss.setAttribute('stdDeviation', '3');
    feGauss.setAttribute('result', 'blur');
    filter.appendChild(feGauss);
    const feMerge = document.createElementNS('http://www.w3.org/2000/svg', 'feMerge');
    const feMN1 = document.createElementNS('http://www.w3.org/2000/svg', 'feMergeNode');
    feMN1.setAttribute('in', 'blur');
    const feMN2 = document.createElementNS('http://www.w3.org/2000/svg', 'feMergeNode');
    feMN2.setAttribute('in', 'SourceGraphic');
    feMerge.appendChild(feMN1);
    feMerge.appendChild(feMN2);
    filter.appendChild(feMerge);
    defs.appendChild(filter);
    svg.appendChild(defs);

    // Draw edges
    edges.forEach(e => {
      const from = positions[e.source_id];
      const to = positions[e.target_id];
      if (!from || !to) return;
      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line.setAttribute('x1', from.x);
      line.setAttribute('y1', from.y);
      line.setAttribute('x2', to.x);
      line.setAttribute('y2', to.y);
      line.setAttribute('stroke', '#94a3b8');
      line.setAttribute('stroke-width', Math.max(0.5, (e.weight || 0.5) * 3));
      line.setAttribute('stroke-opacity', Math.max(0.15, e.relevance || 0.3));
      line.setAttribute('marker-end', 'url(#act-arrow)');
      svg.appendChild(line);
    });

    // Draw nodes
    nodes.forEach(n => {
      const pos = positions[n.id];
      if (!pos) return;
      const r = Math.max(6, Math.min(28, Math.sqrt(n.spikeCount) * 4 + 4));
      const color = getNodeColor(n.type);
      const opacity = Math.max(0.25, Math.min(1, n.potency));

      const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      g.style.cursor = 'pointer';

      if (n.isSeed) {
        const ring = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        ring.setAttribute('cx', pos.x);
        ring.setAttribute('cy', pos.y);
        ring.setAttribute('r', r + 4);
        ring.setAttribute('fill', 'none');
        ring.setAttribute('stroke', '#22c55e');
        ring.setAttribute('stroke-width', '2');
        ring.setAttribute('stroke-dasharray', '4 2');
        ring.setAttribute('filter', 'url(#seed-glow)');
        g.appendChild(ring);
      }

      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', pos.x);
      circle.setAttribute('cy', pos.y);
      circle.setAttribute('r', r);
      circle.setAttribute('fill', color);
      circle.setAttribute('fill-opacity', opacity);
      circle.setAttribute('stroke', color);
      circle.setAttribute('stroke-width', '1.5');
      g.appendChild(circle);

      const label = n.name.length > 16 ? n.name.substring(0, 14) + '...' : n.name;
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', pos.x);
      text.setAttribute('y', pos.y + r + 13);
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('font-size', '10');
      text.setAttribute('fill', '#475569');
      text.textContent = label;
      g.appendChild(text);

      g.addEventListener('mouseenter', (evt) => {
        setTooltip({
          x: evt.clientX,
          y: evt.clientY,
          name: n.name,
          type: n.type,
          spikeCount: n.spikeCount,
          potency: n.potency,
          isSeed: n.isSeed,
        });
      });
      g.addEventListener('mouseleave', () => setTooltip(null));
      g.addEventListener('click', () => onNodeClick?.(n.id));

      svg.appendChild(g);
    });
  }, [seedNodes, firedNodes, synapses, width, height, nodeMap, validEdges, onNodeClick]);

  const totalNodes = Object.keys(nodeMap()).length;
  const totalEdges = validEdges().length;

  if (totalNodes === 0) {
    return <div className="explain-empty">No activation data available for visualization.</div>;
  }

  return (
    <div className="activation-graph" style={{ position: 'relative' }}>
      <h3 className="explain-section-title">Activation Graph</h3>
      <div className="activation-graph__legend">
        <span className="activation-graph__legend-item">
          <span className="activation-graph__dot" style={{ background: TYPE_COLORS.seed }} /> Seed
        </span>
        <span className="activation-graph__legend-item">
          <span className="activation-graph__dot" style={{ background: TYPE_COLORS.decision_outcome }} /> Outcome
        </span>
        <span className="activation-graph__legend-item">
          <span className="activation-graph__dot" style={{ background: TYPE_COLORS.concept }} /> Concept
        </span>
        <span className="activation-graph__legend-item">
          <span className="activation-graph__dot" style={{ background: TYPE_COLORS.and_gate }} /> Gate
        </span>
        <span className="activation-graph__stat">{totalNodes} nodes, {totalEdges} edges</span>
      </div>
      <svg
        ref={svgRef}
        width="100%"
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="activation-graph__svg"
        style={{ background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}
      />
      {tooltip && (
        <div
          className="activation-graph__tooltip"
          style={{
            position: 'fixed',
            left: tooltip.x + 12,
            top: tooltip.y - 10,
            background: '#1e293b',
            color: '#f1f5f9',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            zIndex: 1000,
            pointerEvents: 'none',
            maxWidth: 250,
          }}
        >
          <div style={{ fontWeight: 600 }}>{tooltip.name}</div>
          <div>Type: {tooltip.type}{tooltip.isSeed ? ' (seed)' : ''}</div>
          <div>Spikes: {tooltip.spikeCount}</div>
          <div>Potency: {(tooltip.potency * 100).toFixed(0)}%</div>
        </div>
      )}
    </div>
  );
};

export default ActivationGraph;
